import { isSubTreeKey } from '../../../client/components/NavigationMenu/utils';
import { toUrlFormat } from '..';
import { NAME_COLLISION_MEMBER_TYPES } from './consts';
import { updateTree } from '../../updateTree';
import { verifyResolvingNameConflictsConfig } from './utils';
import { ViewerWrapper } from '../../../Viewer/ViewerWrapper';
import { com } from '../../../generated/proto-generated';

const TEXT_PLACEMENT_OPTIONS =
  com.wixpress.wixdocs.ViewerConfig.NameConflictResolution.TextPlacement;

export const avoidNameCollisionsVisitor = (
  viewer: ViewerWrapper,
  docsTree: any,
) => (node: any) => {
  if (node?.data) {
    const nodeMembers = getNodeMembers(node);
    nodeMembers.forEach((collisionMembers) => {
      const hasCollision = collisionMembers.length > 1;
      if (hasCollision) {
        collisionMembers.forEach((collisionMemberDetails) =>
          handleCollisionIfNeeded(
            collisionMemberDetails,
            node,
            viewer.viewerConfig.resolvingNameConflicts,
            docsTree,
          ),
        );
      }
    });
  }
};

function getNodeMembers(node: any): Map<any, any[]> {
  const nodeMembers: Map<any, any[]> = new Map<any, any[]>();

  addToNodeMembers(node.data?.properties, 'properties', nodeMembers);
  addToNodeMembers(node.data?.operations, 'operations', nodeMembers);
  addToNodeMembers(node.data?.events, 'events', nodeMembers);
  addToNodeMembers(node.data?.relatedGuides, 'relatedGuides', nodeMembers);
  addToNodeMembers(node.data?.messages, 'messages', nodeMembers);
  addToNodeMembers(node.data?.clientEvents, 'clientEvents', nodeMembers);

  addSubCategoriesToNodeMembers(node, nodeMembers);

  return nodeMembers;
}

function addToNodeMembers(
  nodeMember: any[] | undefined = [],
  type: string,
  nodeMembers: Map<any, any[]>,
) {
  nodeMember.forEach((member, index) => {
    nodeMembers.set(member.urlFormat, [
      ...(nodeMembers.get(member.urlFormat) || []),
      {
        urlFormat: member.urlFormat,
        type,
        index,
      },
    ]);
  });
}

function addSubCategoriesToNodeMembers(
  node: any,
  nodeMembers: Map<any, any[]>,
) {
  const subTreeKeys = Object.keys(node).filter(isSubTreeKey);
  subTreeKeys.forEach((key) => {
    const subCategory = node[key];
    const subCategoryUrlFormat = toUrlFormat(subCategory.data.name);
    nodeMembers.set(subCategoryUrlFormat, [
      ...(nodeMembers.get(subCategoryUrlFormat) || []),
      {
        urlFormat: subCategoryUrlFormat,
        type: NAME_COLLISION_MEMBER_TYPES.SUBCATEGORIES,
      },
    ]);
  });
}

function handleCollisionIfNeeded(
  collisionMemberDetails,
  node,
  resolvingNameConflicts,
  docsTree,
) {
  const type = collisionMemberDetails.type;
  const resolvingNameConflictForType = resolvingNameConflicts[type];
  const shouldHandleCollisionForCurrentMemberType =
    resolvingNameConflictForType &&
    (resolvingNameConflictForType.additionalText ||
      resolvingNameConflictForType.textPlacement);

  if (shouldHandleCollisionForCurrentMemberType) {
    updateTreeAfterNameCollisionFix(
      node,
      collisionMemberDetails,
      resolvingNameConflictForType,
      docsTree,
    );
  }
}

function updateTreeAfterNameCollisionFix(
  node,
  collisionMemberDetails,
  resolvingNameConflictForType,
  docsTree,
) {
  const memberType = collisionMemberDetails.type;
  verifyResolvingNameConflictsConfig(resolvingNameConflictForType, memberType);

  const collidedMember = getCollidedMember(node, collisionMemberDetails);
  if (collidedMember) {
    const { additionalText, textPlacement } = resolvingNameConflictForType;
    const newName =
      textPlacement === TEXT_PLACEMENT_OPTIONS.PREFIX
        ? `${additionalText}-${collidedMember.name}`
        : `${collidedMember.name}-${additionalText}`;
    const newUrlFormat = toUrlFormat(newName);

    if (memberType === NAME_COLLISION_MEMBER_TYPES.SUBCATEGORIES) {
      const path = toUrlFormat(
        // eslint-disable-next-line prettier/prettier
        `${collidedMember.memberOf || node.data.urlFormat}.${collidedMember.name}`,
      );
      updateTree(docsTree, path, newUrlFormat, true);
    } else {
      collidedMember.urlFormat = newUrlFormat;
    }
  }
}

function getCollidedMember(node, collisionMemberDetails) {
  const { type, index, urlFormat } = collisionMemberDetails;
  if (type !== NAME_COLLISION_MEMBER_TYPES.SUBCATEGORIES) {
    return node && node.data && node.data[type][index];
  }
  return node[`/${urlFormat}`] && node[`/${urlFormat}`].data;
}
